import React, { createContext, useContext, useState, useEffect } from 'react';
import { setActivePartnerAccount } from '../utils/partnerAccount';
import { loginPath } from '../utils/partnerHost';
import api, { storeAuthToken, clearAuthToken, clearBuyerViewClientId } from '../utils/api';
import { installExportGuard, setExportPolicy } from '../utils/exportGuard';
import { purgeOfflineData } from '../utils/offlineData';
import { UNIVERSAL_MODULES } from '../config/universalModules';
import { equivalentModulePaths } from '../config/modulePathAliases';

const AuthContext = createContext();

const ACCESS_LEVEL_RANK = { view: 1, edit: 2, approve: 3, admin: 4 };

function accessRank(level) {
  if (typeof level === 'number') return level;
  return ACCESS_LEVEL_RANK[String(level || '').toLowerCase()] || 0;
}

function rankedModuleLevels(moduleLevels) {
  if (!moduleLevels || typeof moduleLevels !== 'object' || Array.isArray(moduleLevels)) return {};
  return Object.fromEntries(
    Object.entries(moduleLevels)
      .map(([rawPath, rawLevel]) => {
        const path = String(rawPath || '').trim();
        const level = accessRank(rawLevel);
        if (!path || level < ACCESS_LEVEL_RANK.view) return null;
        return [path.startsWith('/') ? path : `/${path}`, level];
      })
      .filter(Boolean)
  );
}


// The export lock: after every sign-in or session refresh, ask the server what
// this user may download so the browser refuses its own links the same way.
const loadExportPolicy = () =>
  api.get('/export-policy').then((r) => setExportPolicy(r.data)).catch(() => { /* server stays the authority */ });
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { installExportGuard(); }, []);

  useEffect(() => {
    // Auth via httpOnly SSO cookie — no localStorage token needed
    const refreshSession = () =>
      api.get('/auth/me')
        .then(r => {
          // Persist the refreshed token so the cross-domain Bearer fallback (api.js
          // localStorage 'aone_token') also gets updated modules. Without this,
          // /auth/me refreshes the SSO cookie but the backend prefers the stale
          // Authorization header (auth.js: header || cookie), pinning old modules
          // until a full re-login. Module grants must propagate without re-login.
          if (r.data?.token) storeAuthToken(r.data.token);
          setUser(r.data);
          loadExportPolicy();
        })
        .catch((err) => {
          // On timeout (or any error), proceed as logged-out — better than infinite splash
          if (err?.code === 'ECONNABORTED') {
            console.error('[auth] /auth/me timed out — backend may be slow or down. Proceeding as logged-out.');
          }
          setUser(null);
        });

    refreshSession().finally(() => setLoading(false));

    // Auto-refresh every 3 hours — re-validates session, auto-logs out if deactivated
    const interval = setInterval(refreshSession, 3 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    if (data.mustResetPassword) {
      return { mustResetPassword: true, resetToken: data.resetToken, message: data.message };
    }
    if (data.requires2FA) {
      return { requires2FA: true, challengeToken: data.challengeToken, userId: data.userId };
    }
    // Persist token for cross-domain hosts where the SSO cookie is rejected by
    // the browser. api.js picks this up
    // as an Authorization: Bearer header on every subsequent request.
    if (data.token) storeAuthToken(data.token);
    setUser(data.user);
    loadExportPolicy();
    return data.user;
  };

  const forceResetPassword = async (resetToken, newPassword) => {
    const { data } = await api.put('/auth/force-reset-password', { resetToken, newPassword });
    if (data.token) storeAuthToken(data.token);
    setUser(data.user);
    loadExportPolicy();
    return data.user;
  };

  const verify2FA = async (userId, token, challengeToken) => {
    const { data } = await api.post('/auth/2fa/verify', { challengeToken, userId, token });
    // Password-expiry / admin-forced reset is applied after the second factor too,
    // so this can legitimately come back without a session token. Returning early
    // avoids setUser(undefined), which would look like a successful login.
    if (data.mustResetPassword) {
      return { mustResetPassword: true, resetToken: data.resetToken, message: data.message };
    }
    if (data.token) storeAuthToken(data.token);
    setUser(data.user);
    loadExportPolicy();
    return data.user;
  };

  // Passkey sign-in. The server answers in exactly the same shapes as the
  // password and Google paths — including requires2FA — so a passkey does not
  // become a way around the second factor.
  // `autofill: true` runs the request as conditional mediation: the browser
  // offers a passkey inside the email field's autofill list ONLY if this
  // device has one for the site, and shows nothing otherwise. The explicit
  // button uses the modal request; on a device with no passkey the browser
  // answers that with its phone/QR sheet, which is why the button must not be
  // the first thing that fires.
  const passkeyLogin = async ({ autofill = false } = {}) => {
    const { startAuthentication } = await import('@simplewebauthn/browser');
    const { data: options } = await api.post('/passkey/login-options', {});
    const assertion = await startAuthentication({ optionsJSON: options, useBrowserAutofill: autofill });
    const { data } = await api.post('/passkey/login-verify', { body: assertion });
    if (data.mustResetPassword) {
      return { mustResetPassword: true, resetToken: data.resetToken, message: data.message };
    }
    if (data.requires2FA) {
      return { requires2FA: true, challengeToken: data.challengeToken, userId: data.userId };
    }
    if (data.token) storeAuthToken(data.token);
    setUser(data.user);
    loadExportPolicy();
    return data.user;
  };

  const googleLogin = async (credential) => {
    const { data } = await api.post('/auth/google', { credential });
    if (data.requires2FA) {
      return { requires2FA: true, challengeToken: data.challengeToken, userId: data.userId };
    }
    if (data.token) storeAuthToken(data.token);
    setUser(data.user);
    loadExportPolicy();
    return data.user;
  };

  const logout = () => {
    setActivePartnerAccount(''); // a partner's chosen company does not outlive the session
    setUser(null);
    // Logout used to remove the token and nothing else. The offline snapshots
    // (dashboard, tasks, approvals, notifications), the queued approval writes
    // and the buyer-impersonation scope all survived it, so on a shared machine
    // the next person to sign in saw the previous user's work — and a queued
    // approval could replay under their session.
    clearAuthToken();
    purgeOfflineData();
    clearBuyerViewClientId();
    api.post('/auth/logout').catch((e) => { console.error('[auth] logout server cleanup error:', e.message); });
    window.location.href = loginPath();
  };

  const userLevelForPath = (rawPath) => {
    if (!user) return false;
    const path = String(rawPath || '').split('?')[0]; // gate by base path so query-string deep-links (e.g. /formulations?view=benchmarks) inherit the page's access
    const modules = user.modules || [];
    const levelMap = rankedModuleLevels(user.moduleLevels);

    if (user.role === 'founder' || modules.includes('*')) return ACCESS_LEVEL_RANK.admin;

    const paths = equivalentModulePaths(path);
    let best = 0;

    const applyLegacyModuleLevel = (candidate) => {
      if (modules.some(m => candidate === m || candidate.startsWith(m + '/'))) {
        // Universal paths are merged into every user's modules at login, so bare
        // membership there is only worth VIEW — explicit moduleLevels grant edit+.
        // Mirrors backend _userLevelForModule (admin-view escalation fix).
        const rank = UNIVERSAL_MODULES.includes(candidate) ? ACCESS_LEVEL_RANK.view : ACCESS_LEVEL_RANK.edit;
        best = Math.max(best, rank);
      }
    };
    const applyExplicitLevel = (candidate) => {
      Object.entries(levelMap).forEach(([m, level]) => {
        if (candidate === m || candidate.startsWith(m + '/')) best = Math.max(best, level);
      });
    };

    paths.forEach((candidate) => {
      applyLegacyModuleLevel(candidate);
      applyExplicitLevel(candidate);
    });

    if (user.role !== 'security_guard' && paths.some(candidate => UNIVERSAL_MODULES.includes(candidate))) {
      best = Math.max(best, ACCESS_LEVEL_RANK.view);
    }

    return best;
  };

  const hasModuleLevel = (rawPath, requiredLevel = 'view') => {
    const required = accessRank(requiredLevel);
    return userLevelForPath(rawPath) >= required;
  };

  const canAccess = (rawPath) => {
    return hasModuleLevel(rawPath, 'view');
  };

  return (
    <AuthContext.Provider value={{ user, login, verify2FA, googleLogin, passkeyLogin, forceResetPassword, logout, loading, canAccess, hasModuleLevel }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
